import DescriptionIcon from "@mui/icons-material/Description";
import VideocamIcon from "@mui/icons-material/Videocam";
import {
  Card,
  GridLegacy as Grid,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import { useQueryClient } from "@tanstack/react-query";
import { assocPath, pick, pipe } from "ramda";
import { useCallback, useEffect, useState } from "react";
import { TextFieldHelperText } from "../../components/Forms/TextFieldHelperText";
import { Icon } from "../../components/Icon";
import { generalMessages } from "../../components/messages";
import { useMsg } from "../../components/Msg/Msg";
import { H2, P } from "../../components/Typography";
import { primary50, primary500 } from "../../theme";
import { useMyMutation } from "../Authorization/reactQueryWrappers";
import { QueryRenderer } from "../QM/QueryRenderer";
import { AiResponseAccordion } from "./AiResponseAccordion";
import {
  quickAiHelpKey,
  userArticlesKey,
  userPreviewsKey,
} from "./DashboardRightMenu";
import { dashboardMessages } from "./messages";

const useQuickAiHelpMutation = () => {
  const queryClient = useQueryClient();
  return useMyMutation({
    // TODO: persist variables during runtime if possible?
    // mutationKey: ["quick-ai-help-mutation"],
    fetchDef: {
      method: "POST",
      url: `/api/latest/user-insight/dashboard`,
      from: pick(["query"]),
    },
    invalidate: [{ queryKey: ["user-insight"] }],
    invalidateOn: ["settled"],
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        ["user-insight"],
        pipe(
          assocPath([quickAiHelpKey, "isPending"], true),
          assocPath([userPreviewsKey, "isPending"], true),
          assocPath([userArticlesKey, "isPending"], true),
        ),
      );
    },
  });
};

const minLength = 1;
const maxLength = 500;

export const AiHelperCard = ({
  insightsQuery,
  isCollapsed, // TODO: when videos+articles are generated after session, display this card collapsed. BE needed
}) => {
  const dashboardMsg = useMsg({ dict: dashboardMessages });
  const generalMsg = useMsg({ dict: generalMessages });
  const [textInput, setTextInput] = useState("");
  const [expanded, setExpanded] = useState(false);
  const handleResultExpandedChange = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);
  const mutation = useQuickAiHelpMutation();
  // Not possible to say if results match current query
  // We assume that after succesful mutation, the text is up to date when not pending
  const isInputResultReceived =
    !!textInput.length &&
    textInput === mutation.variables?.query &&
    mutation.isSuccess &&
    !insightsQuery.data?.[quickAiHelpKey]?.isPending &&
    !!insightsQuery.data?.[quickAiHelpKey]?.text;
  const submitDisabled =
    textInput.trim().length < minLength ||
    textInput.length > maxLength ||
    !insightsQuery.data ||
    insightsQuery.data?.[quickAiHelpKey]?.isPending ||
    isInputResultReceived;
  const handleSubmit = useCallback(() => {
    mutation.mutate({ query: textInput });
  }, [textInput, mutation]);

  useEffect(() => {
    if (isInputResultReceived) setExpanded(true);
  }, [isInputResultReceived]);

  console.log("[AiHelperCard.rndr]", {
    textInput,
    mutation,
    insightsQuery,
  });

  return (
    <Card sx={{ minHeight: "150px", p: "24px" }}>
      <H2 sx={{ mb: 2 }}>{dashboardMsg("dashboard.ai.help-header")}</H2>
      <P sx={{ mb: 2 }}>{dashboardMsg("dashboard.ai.help.second-header")}</P>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <TextField
          variant="outlined"
          multiline
          fullWidth
          placeholder={dashboardMsg("dashboard.ai.help.placeholder")}
          value={textInput}
          onChange={(e) => setTextInput(e.target.value)}
          disabled={mutation.isPending}
          helperText={
            <TextFieldHelperText
              charCounter={{ value: textInput.length, max: maxLength }}
            />
          }
          onKeyDown={(e) => {
            // TODO: use in Messages
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();

              if (!submitDisabled && !mutation.isPending) {
                handleSubmit();
              }
            }
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    color="primary"
                    disabled={submitDisabled}
                    loading={mutation.isPending}
                    onClick={handleSubmit}
                    edge="end"
                  >
                    <Icon name="Send" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <QueryRenderer
          query={insightsQuery}
          loaderEl={null} // When we don't have result, nothing is displayed. Prevent loader -> nothing
          success={({ data }) => {
            return (
              <AiResponseAccordion
                isPending={insightsQuery.data?.[quickAiHelpKey]?.isPending}
                summary={dashboardMsg("dashboard.ai.help.suggestion")}
                text={data[quickAiHelpKey]?.text}
                expanded={expanded}
                onChange={handleResultExpandedChange}
                hideEmpty={!insightsQuery.data?.[quickAiHelpKey]?.isPending}
                markdown
              />
            );
          }}
        />
        {/* <FormControlLabel control={<Checkbox />} label={dashboardMsg("dashboard.ai.help.checkbox")} sx={{ alignSelf: "flex-start" }} /> */}
        {/* <Button variant="contained" disabled={submitDisabled} loading={mutation.isPending} onClick={handleSubmit} sx={{ alignSelf: "flex-start", mt: 2 }} startIcon={<Icon name="SendOutlined" />} > {generalMsg("general.confirm")} </Button> */}
      </Box>
    </Card>
  );
};

const AiPlaceholderCard = ({
  iconName, // TODO
  Icon,
  color,
  backgroundColor,
  title,
  description,
  fullHeight,
}) => {
  return (
    <Card
      sx={{
        minHeight: "150px",
        p: "24px",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        flexDirection: "column",
        textAlign: "center",
        height: fullHeight ? "100%" : undefined,
      }}
    >
      <Box
        sx={{
          backgroundColor,
          p: 2,
          mb: 2,
          borderRadius: 1,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon sx={{ color }} />
      </Box>
      <H2 sx={{ mb: 1 }}>{title}</H2>
      <P sx={{ textAlign: "center" }}>{description}</P>
    </Card>
  );
};

export const AiHelperPlaceholders = () => {
  const dashboardMsg = useMsg({ dict: dashboardMessages });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <AiPlaceholderCard
          Icon={VideocamIcon}
          color={primary500}
          backgroundColor={primary50}
          title={dashboardMsg("dashboard.ai.help.header.one")}
          description={dashboardMsg("dashboard.ai.help.text.one")}
          fullHeight
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <AiPlaceholderCard
          Icon={DescriptionIcon}
          color={"#3162e4"}
          backgroundColor={"#ddeafd"}
          title={dashboardMsg("dashboard.ai.help.header.two")}
          description={dashboardMsg("dashboard.ai.help.text.two")}
          fullHeight
        />
      </Grid>
      {/* <Grid item xs={12} sm={6}>
        <AiPlaceholderCard
          Icon={AutoAwesomeIcon}
          color={"#42a053"}
          backgroundColor={"#e1fbe8"}
          title={dashboardMsg("dashboard.ai.help.header.three")}
          description={dashboardMsg("dashboard.ai.help.text.three")}
          fullHeight
        />
      </Grid> */}
    </Grid>
  );
};

export const AiHelper = () => {
  return (
    <Grid container>
      <Grid item xs={12} sm={12} sx={{ mb: 3 }}>
        <AiHelperCard />
      </Grid>
      <AiHelperPlaceholders />
    </Grid>
  );
};
